import { trackNavigationClick } from '@ecomm/cdp-tracking'
import { useLocale } from '@ecomm/data-hooks'
import { useOnKeyUp } from '@ecomm/shared-hooks'
import { useCartQuantity } from '@ecomm/shared-hooks'
import { useMemo, useRef, useState } from 'react'
import { useTracking } from 'react-tracking'
import type { HeaderProps } from './types'

import { useAreExpertsAvailable } from '@ecomm/scout'
import { useCameraOnlyLPCTAExperience } from '@ecomm/shared-ninetailed'
import { usePhoneAndMonitoringInNavExperience } from '@ecomm/shared-ninetailed-phone'
import { CourtesyNav } from './CourtesyNav'
import { HamburgerButton } from './HamburgerButton'
import { LogoLink } from './LogoLink'
import { MainNav } from './MainNav'
import { SideNav } from './SideNav'
import { getCourtesyNavItems } from './data'

export function FullHeader({
  images,
  quoteWizard
}: HeaderProps & {
  readonly id?: string
}) {
  const { isVariant: isPhoneVariant, status } =
    usePhoneAndMonitoringInNavExperience()
  const areExpertsAvailable = useAreExpertsAvailable(8, 22)
  const { isCameraOnlyVariant } = useCameraOnlyLPCTAExperience()

  const locale = useLocale()

  const [showSideNav, setShowSideNav] = useState(false)
  const toggleSideNav = (track = false) => {
    track &&
      trackEvent({
        event: 'navigation-link',
        eventAction: showSideNav ? 'close' : 'open',
        eventCategory: 'navigation',
        label: 'hamburger'
      })
    track &&
      trackNavigationClick({
        action: showSideNav ? 'close' : 'open',
        navElement: 'hamburger'
      })
    setShowSideNav(prev => !prev)
  }
  const closeSideNav = () => setShowSideNav(false)

  const headerRef = useRef<HTMLDivElement>(null)

  useOnKeyUp('Escape', () => setShowSideNav(false))

  const { cartQuantity } = useCartQuantity()

  const { trackEvent } = useTracking()

  const top = headerRef?.current?.offsetTop || 0

  const courtesyNavItems = useMemo(
    () =>
      getCourtesyNavItems(
        locale,
        isPhoneVariant,
        status,
        areExpertsAvailable,
        isCameraOnlyVariant
      ),
    [areExpertsAvailable, isPhoneVariant, locale, status, isCameraOnlyVariant]
  )

  return (
    <header
      className="bg-neutral-black relative w-full text-inherit lg:z-20"
      ref={headerRef}
    >
      <nav className="py-p-4 max-w-8xl mx-auto flex h-[50px] items-center gap-4 px-3 max-[1125px]:justify-between md:gap-8 md:px-8 md:py-0 min-[1126px]:h-[78px]">
        <HamburgerButton onClick={toggleSideNav} open={showSideNav} />
        <LogoLink type="full" />
        <MainNav images={images} quoteWizard={quoteWizard} />
        <CourtesyNav
          cartQuantity={cartQuantity}
          images={images}
          items={courtesyNavItems}
          quoteWizard={quoteWizard}
        />
      </nav>
      <SideNav
        areExpertsAvailable={areExpertsAvailable}
        close={closeSideNav}
        images={images}
        quoteWizard={quoteWizard}
        show={showSideNav}
        toggle={toggleSideNav}
        top={top}
      />
    </header>
  )
}
